import { Link } from "react-router-dom";
import { ShieldCheckIcon, BuildingLibraryIcon, UsersIcon, ArrowRightIcon, BoltIcon } from "@heroicons/react/24/outline";
import BannerSchool from "../../assets/banner-school.png";

const stats = [
  { Icon: BuildingLibraryIcon, value: "500+", label: "Instituições" },
  { Icon: UsersIcon,           value: "50k+", label: "Estudantes" },
  { Icon: ShieldCheckIcon,     value: "100%", label: "Seguro" },
];

export default function Banner() {
  return (
    <section
      className="relative py-28 max-md:py-20"
      style={{ background: "linear-gradient(135deg, #001627 0%, #002849 45%, #0569a3 100%)" }}
    >
      {/* Clipped decorative layer (overflow-hidden here, not on section) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Dot grid */}
        <div className="absolute inset-0" style={{
          backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.06) 1px, transparent 1px)",
          backgroundSize: "28px 28px",
        }} />
        {/* Glow orbs */}
        <div className="absolute -top-24 -right-24 w-125 h-125 rounded-full"
          style={{ background: "radial-gradient(circle, rgba(43,171,227,0.22) 0%, transparent 65%)" }} />
        <div className="absolute -bottom-16 -left-16 w-80 h-80 rounded-full"
          style={{ background: "radial-gradient(circle, rgba(5,105,163,0.3) 0%, transparent 65%)" }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full"
          style={{ background: "radial-gradient(circle, rgba(43,171,227,0.06) 0%, transparent 70%)" }} />
      </div>

      <div className="relative max-w-6xl mx-auto px-6">
        <div className="flex items-center gap-16 max-md:flex-col">

          {/* ── Left ── */}
          <div className="flex-1 max-w-xl">
            {/* Live badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 backdrop-blur-sm text-white/80 text-xs font-medium px-4 py-1.5 rounded-full mb-8">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              Plataforma de pagamentos escolares · Angola
            </div>

            <h1 className="text-5xl font-extrabold text-white leading-[1.1] mb-5 max-md:text-3xl">
              O crescimento da sua{" "}
              <span className="text-transparent bg-clip-text"
                style={{ backgroundImage: "linear-gradient(90deg, #2babe3, #7dd3fc)" }}>
                instituição
              </span>{" "}
              começa aqui
            </h1>

            <p className="text-white/60 text-lg leading-relaxed mb-10 max-w-lg">
              Receba propinas em tempo real. Envie convocatórias, gerencie pagamentos
              e acompanhe a sua instituição de qualquer lugar — fácil, seguro e rápido.
            </p>

            <div className="flex flex-wrap gap-4">
              <Link to="/register"
                className="inline-flex items-center gap-2 text-white font-semibold px-8 py-4 rounded-full text-sm transition-all hover:opacity-90 hover:scale-105 shadow-lg shadow-blue/30"
                style={{ background: "linear-gradient(90deg, #2babe3, #016fb9)" }}>
                Cadastra-se Agora
                <ArrowRightIcon className="w-4 h-4" />
              </Link>
              <a href="#services"
                className="inline-flex items-center gap-2 border border-white/20 hover:bg-white/10 hover:border-white/40 text-white/80 hover:text-white font-medium px-8 py-4 rounded-full text-sm transition-all">
                Ver Serviços
              </a>
            </div>

            {/* Stats */}
            <div className="flex gap-10 mt-12 pt-10 border-t border-white/10 max-sm:gap-6">
              {stats.map(({ Icon, value, label }) => (
                <div key={label} className="group">
                  <Icon className="w-5 h-5 text-blue-light mb-2 group-hover:scale-110 transition-transform" />
                  <p className="text-white font-extrabold text-xl leading-none">{value}</p>
                  <p className="text-white/45 text-xs mt-1">{label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── Right ── */}
          <div className="flex-1 relative flex justify-center items-center max-md:hidden">
            {/* Glow ring behind image */}
            <div className="absolute w-96 h-96 rounded-full"
              style={{ background: "radial-gradient(circle, rgba(43,171,227,0.18) 0%, transparent 70%)" }} />

            {/* Photo with ring */}
            <div className="relative">
              <div className="absolute -inset-1 rounded-3xl pointer-events-none"
                style={{ background: "linear-gradient(135deg, rgba(43,171,227,0.4), rgba(1,111,185,0.2), transparent 60%)" }} />
              <img
                src={BannerSchool}
                alt="Estudantes PIA"
                className="relative w-full max-w-md rounded-2xl object-cover shadow-2xl"
              />

              {/* Overlay gradient bottom of photo */}
              <div className="absolute bottom-0 left-0 right-0 h-24 rounded-b-2xl pointer-events-none"
                style={{ background: "linear-gradient(to top, rgba(0,22,39,0.5), transparent)" }} />

              {/* Floating card — pagamento aprovado */}
              <div className="absolute -bottom-4 -left-6 flex items-center gap-3 bg-white/12 backdrop-blur-md border border-white/20 rounded-2xl px-4 py-3 shadow-2xl">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: "linear-gradient(135deg, #10b981, #059669)" }}>
                  <ShieldCheckIcon className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-white text-xs font-bold">Pagamento aprovado</p>
                  <p className="text-white/55 text-xs">Propina · 25.000 Kz</p>
                </div>
                <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse ml-1" />
              </div>

              {/* Floating card — top right: live students */}
              <div className="absolute -top-4 -right-6 flex items-center gap-2.5 bg-white/12 backdrop-blur-md border border-white/20 rounded-xl px-4 py-2.5 shadow-xl">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ background: "linear-gradient(135deg, #2babe3, #016fb9)" }}>
                  <BoltIcon className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-white text-[11px] font-bold">3 novos pagamentos</p>
                  <p className="text-white/50 text-[10px]">nos últimos 5 min</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Wave */}
      <div className="absolute bottom-0 left-0 right-0 leading-none" style={{ lineHeight: 0 }}>
        <svg viewBox="0 0 1440 60" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none"
          style={{ display: "block", width: "100%", height: 60 }}>
          <path d="M0,60 L0,30 C180,55 360,5 540,25 C720,45 900,5 1080,25 C1260,45 1350,15 1440,30 L1440,60 Z" fill="#f8fafc" />
        </svg>
      </div>
    </section>
  );
}
